import type { FollowedUpInformationType } from '~/components/Project/model';
import type { ProjectType } from '~/model/ProjectType';
import { useSearchParams } from 'next/navigation';
import { useCallback, useEffect, useState } from 'react';
import { fetchCollection, fetchItem } from '@/app/actions/server';
import { useApiErrorContext, useAuthenticatedContext } from '@/app/components/Provider';
import { dataLayerPush } from '@/app/utils/analytics/datalayer';
import { createQueryString } from '@/app/utils/url/search-params';
import { usePathname, useRouter } from '@/i18n/routing';
import { AnalyticEvents } from '~/const/appConst';
import { RequestStatus, useRequestState } from '~/hooks/request/useRequestState';
import { isAuthenticatedAsMainUser } from '~/utils/user/user';

export function useProjectFollow(project: ProjectType) {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();

  const { setError } = useApiErrorContext();

  const {
    user,
    isAuthenticated,
    isAuthenticating,
    redirectToLogin,
  } = useAuthenticatedContext();

  const {
    handleError,
    handleSuccess,
    startRequest,
    status,
  } = useRequestState();

  const [followedUpInformation, setFollowedUpInformation] = useState<FollowedUpInformationType>();

  const { id, active } = followedUpInformation || {};
  const searchParamsKey = 'follow';
  const isFollowedBtnDisplayed = !isAuthenticating || searchParams.get(searchParamsKey);

  const toggle = async () => {
    if (isAuthenticatedAsMainUser(user, isAuthenticated)) {
      startRequest();

      const response = await fetchItem<FollowedUpInformationType>({
        method: id ? 'PUT' : 'POST',
        pathname: id ? `followed_up_projects/${id}` : 'followed_up_projects',
        revalidate: 0,
        body: {
          project: project['@id'],
          active: !active,
        },
      });

      if (response['hydra:description']) {
        handleError();
        return setError(response);
      }

      handleSuccess();
      setFollowedUpInformation(response);

      dataLayerPush({
        event: AnalyticEvents.FOLLOW_UP,
        page_project_id: `${project.id}`,
        user_id: `${user.id}`,
      });
    } else {
      await redirectToLogin(`${pathname}?${searchParamsKey}=1`);
    }
  };

  const fetchFollowedUpInformation = useCallback(async () => {
    if (!project?.id || !isAuthenticatedAsMainUser(user, isAuthenticated))
      return;

    startRequest();

    const response = await fetchCollection<FollowedUpInformationType>({
      pathname: 'users/me/followed_up_projects',
      revalidate: 0,
      searchParams: {
        project: project.id,
      },
    });

    if (response['hydra:description']) {
      handleError();
      return setError(response);
    }

    const item = response['hydra:member'][0];

    setFollowedUpInformation(item);
    handleSuccess();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user, project?.id]);

  const toggleAfterLogin = useCallback(async () => {
    if (
      !isAuthenticatedAsMainUser(user, isAuthenticated)
      || !+searchParams.get(searchParamsKey)
      || !followedUpInformation
    ) {
      return;
    }

    if (!followedUpInformation?.active) {
      await toggle();
    }

    router.replace(`${pathname}?${createQueryString(searchParamsKey, null, searchParams)}`, {
      scroll: false,
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAuthenticated,
    followedUpInformation,
    searchParams,
    user,
  ]);

  useEffect(() => {
    fetchFollowedUpInformation();
  }, [fetchFollowedUpInformation]);

  useEffect(() => {
    toggleAfterLogin();
  }, [toggleAfterLogin]);

  return {
    followed: active,
    isFollowedBtnDisplayed,
    isPending: status === RequestStatus.PENDING,
    toggle,
  };
}
